import React, { useState } from 'react';
import PropTypes from 'prop-types';
import config from '../config';

function SpecialistsSection({ specialists }) {
  const [showDoctors, setShowDoctors] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userLocation, setUserLocation] = useState('');

  if (specialists.length === 0) return null;

  const getRelevantSpecialty = () => {
    // Extract the most relevant specialty from the specialists list
    const specialistText = specialists.join(' ').toLowerCase();
    
    if (specialistText.includes('cardiologist') || specialistText.includes('heart')) {
      return 'Cardiologist';
    } else if (specialistText.includes('endocrinologist') || specialistText.includes('diabetes') || specialistText.includes('thyroid')) {
      return 'Endocrinologist';
    } else if (specialistText.includes('hematologist') || specialistText.includes('blood')) {
      return 'Hematologist';
    } else if (specialistText.includes('nephrologist') || specialistText.includes('kidney')) {
      return 'Nephrologist';
    } else if (specialistText.includes('gastroenterologist') || specialistText.includes('digestive')) {
      return 'Gastroenterologist';
    } else if (specialistText.includes('rheumatologist') || specialistText.includes('joint')) {
      return 'Rheumatologist';
    } else if (specialistText.includes('nutritionist') || specialistText.includes('diet')) {
      return 'Nutritionist';
    } else {
      return 'Primary Care Physician';
    }
  };

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            try {
              // Convert coordinates to city name using a geocoding service
              const response = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`);
              const data = await response.json();
              const location = `${data.city}, ${data.principalSubdivision}, ${data.countryName}`;
              resolve(location);
            } catch (error) {
              // Fallback to coordinates if geocoding fails
              resolve(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
            }
          },
          (error) => {
            reject(error);
          }
        );
      } else {
        reject(new Error('Geolocation is not supported by this browser.'));
      }
    });
  };

  const parseDoctorsFromText = (text) => {
    try {
      // First try to parse as JSON
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch (e) {
      // If JSON parsing fails, try to extract structured information from text
      console.log('JSON parsing failed, attempting text parsing...');
    }

    // Try to find JSON array in the text
    const jsonArrayMatch = text.match(/\[\s*\{[\s\S]*\}\s*\]/);
    if (jsonArrayMatch) {
      try {
        const parsed = JSON.parse(jsonArrayMatch[0]);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch (e) {
        console.log('Extracted JSON array parsing failed');
      }
    }

    // Fallback: Parse text manually
    const doctors = [];
    const lines = text.split('\n').filter(line => line.trim());
    
    let currentDoctor = {};
    let doctorCount = 0;
    
    for (const line of lines) {
      const cleanLine = line.trim();
      
      // Skip empty lines and common headers
      if (!cleanLine || 
          cleanLine.toLowerCase().includes('here are') || 
          cleanLine.toLowerCase().includes('nearest doctors') ||
          cleanLine.toLowerCase().includes('search results')) {
        continue;
      }

      // Try to detect doctor entries by number or "Dr."
      if (cleanLine.match(/^\d+\.\s*Dr\./) || cleanLine.startsWith('Dr.') || cleanLine.match(/^\**Dr\./)) {
        // Save previous doctor if exists
        if (Object.keys(currentDoctor).length > 0) {
          doctors.push(currentDoctor);
        }
        
        // Start new doctor
        currentDoctor = {
          name: cleanLine.replace(/^\d+\.\s*/, '').replace(/^\**/, '').trim(),
          practice: '',
          address: '',
          phone: '',
          distance: '',
          direction_link: '',
          specialization: '',
          rating: ''
        };
        doctorCount++;
      } else if (Object.keys(currentDoctor).length > 0) {
        // Try to categorize the information
        const lowerLine = cleanLine.toLowerCase();
        
        if (lowerLine.includes('practice') || lowerLine.includes('hospital') || lowerLine.includes('clinic')) {
          currentDoctor.practice = cleanLine.replace(/^[\*\-\•]\s*/, '').replace(/practice:\s*/i, '').trim();
        } else if (lowerLine.includes('address') || lowerLine.includes('location')) {
          currentDoctor.address = cleanLine.replace(/^[\*\-\•]\s*/, '').replace(/address:\s*/i, '').trim();
        } else if (lowerLine.includes('phone') || lowerLine.includes('tel') || cleanLine.match(/\d{3}[-.\s]?\d{3}[-.\s]?\d{4}/)) {
          currentDoctor.phone = cleanLine.replace(/^[\*\-\•]\s*/, '').replace(/phone:\s*/i, '').trim();
        } else if (lowerLine.includes('distance') || lowerLine.includes('miles') || lowerLine.includes('km')) {
          currentDoctor.distance = cleanLine.replace(/^[\*\-\•]\s*/, '').replace(/distance:\s*/i, '').trim();
        } else if (lowerLine.includes('specialization') || lowerLine.includes('specialty')) {
          currentDoctor.specialization = cleanLine.replace(/^[\*\-\•]\s*/, '').replace(/specialization:\s*/i, '').trim();
        } else if (lowerLine.includes('rating') || lowerLine.includes('stars') || cleanLine.match(/\d+\.\d+/)) {
          currentDoctor.rating = cleanLine.replace(/^[\*\-\•]\s*/, '').replace(/rating:\s*/i, '').trim();
        } else if (lowerLine.includes('maps.google') || lowerLine.includes('directions')) {
          currentDoctor.direction_link = cleanLine.includes('http') ? cleanLine : `https://maps.google.com/maps?q=${encodeURIComponent(currentDoctor.name + ' ' + currentDoctor.address)}`;
        }
      }
    }
    
    // Add the last doctor
    if (Object.keys(currentDoctor).length > 0) {
      doctors.push(currentDoctor);
    }

    // If we couldn't parse any doctors, create dummy entries with available info
    if (doctors.length === 0 && text.length > 10) {
      const specialty = getRelevantSpecialty();
      return [{
        name: `${specialty} Search Results`,
        practice: 'Multiple practices found',
        address: userLocation || 'Your location',
        phone: 'Please search online for contact details',
        distance: 'Various distances',
        direction_link: `https://maps.google.com/maps?q=${encodeURIComponent(specialty + ' near ' + userLocation)}`,
        specialization: specialty,
        rating: 'Please check online reviews',
        rawInfo: text.substring(0, 500) + '...'
      }];
    }

    // Fill in missing direction links
    doctors.forEach(doctor => {
      if (!doctor.direction_link || !doctor.direction_link.startsWith('http')) {
        const searchQuery = `${doctor.name} ${doctor.practice} ${doctor.address}`.trim();
        doctor.direction_link = `https://maps.google.com/maps?q=${encodeURIComponent(searchQuery)}`;
      }
      
      // Ensure all required fields have values
      doctor.name = doctor.name || 'Doctor Name Not Available';
      doctor.practice = doctor.practice || 'Practice information not available';
      doctor.address = doctor.address || 'Address not available';
      doctor.phone = doctor.phone || 'Phone number not available';
      doctor.distance = doctor.distance || 'Distance not available';
      doctor.specialization = doctor.specialization || getRelevantSpecialty();
      doctor.rating = doctor.rating || 'Rating not available';
    });

    return doctors.slice(0, 3); // Return max 3 doctors
  };

  const handleFindDoctors = async () => {
    setLoading(true);
    setError('');
    
    try {
      let location = userLocation;
      
      // If no manual location entered, try to get current location
      if (!location) {
        try {
          location = await getCurrentLocation();
          setUserLocation(location);
        } catch (geoError) {
          setError('Please enter your location manually or enable location services.');
          setLoading(false);
          return;
        }
      }

      const specialty = getRelevantSpecialty();
      
      const response = await fetch(`${config.API_BASE_URL}/doctors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          specialty: specialty,
          location: location
        }),
      });

      const result = await response.json();

      if (result.success) {
        let parsedDoctors = [];
        
        if (result.doctors && Array.isArray(result.doctors)) {
          parsedDoctors = result.doctors;
        } else if (result.doctors_text) {
          parsedDoctors = parseDoctorsFromText(result.doctors_text);
        } else if (result.doctors && typeof result.doctors === 'string') {
          parsedDoctors = parseDoctorsFromText(result.doctors);
        }
        
        if (parsedDoctors.length > 0) {
          setDoctors(parsedDoctors);
          setShowDoctors(true);
        } else {
          setError(`No ${specialty} doctors found in ${location}. Try a different location or search online.`);
        }
      } else {
        setError(result.error || 'Failed to find doctors');
      }
    } catch (err) {
      console.error('Error finding doctors:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="specialists-section">
      <h3>👨‍⚕️ Recommended Healthcare Specialists</h3>
      <div className="specialists-list">
        {specialists.map((specialist, index) => (
          <div key={index} className="specialist-item">
            <span className="specialist-icon">🏥</span>
            <span className="specialist-text">{specialist}</span>
          </div>
        ))}
      </div>
      
      <div className="find-doctors-section">
        <div className="location-input-container">
          <input
            type="text"
            value={userLocation}
            onChange={(e) => setUserLocation(e.target.value)}
            placeholder="Enter your city/location (or we'll detect it)"
            className="location-input"
          />
        </div>
        
        <button 
          className="find-doctors-button"
          onClick={handleFindDoctors}
          disabled={loading}
        >
          {loading ? (
            <div className="button-loading-content">
              <div className="loading-spinner"></div>
              <span>Finding Doctors...</span>
            </div>
          ) : (
            `🔍 Find Nearest ${getRelevantSpecialty()}`
          )}
        </button>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
      </div>

      {showDoctors && doctors.length > 0 && (
        <div className="doctors-results">
          <h4>📍 Nearest Doctors Found:</h4>
          <div className="doctors-grid">
            {doctors.map((doctor, index) => (
              <div key={index} className="doctor-card">
                <div className="doctor-header">
                  <h5 className="doctor-name">{doctor.name}</h5>
                  {doctor.rating && doctor.rating !== 'Rating not available' && (
                    <span className="doctor-rating">⭐ {doctor.rating}</span>
                  )}
                </div>
                
                <div className="doctor-details">
                  <p className="doctor-practice">🏥 {doctor.practice}</p>
                  <p className="doctor-specialization">🎯 {doctor.specialization}</p>
                  <p className="doctor-address">📍 {doctor.address}</p>
                  <p className="doctor-phone">📞 {doctor.phone}</p>
                  <p className="doctor-distance">📏 {doctor.distance}</p>
                </div>
                
                <div className="doctor-actions">
                  <a 
                    href={doctor.direction_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="directions-button"
                  >
                    🗺️ Get Directions
                  </a>
                </div>
                
                {doctor.rawInfo && (
                  <div className="doctor-raw-info">
                    <details>
                      <summary>View additional information</summary>
                      <p style={{fontSize: '12px', color: '#666', marginTop: '10px'}}>
                        {doctor.rawInfo}
                      </p>
                    </details>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

SpecialistsSection.propTypes = {
  specialists: PropTypes.array.isRequired,
};

export default SpecialistsSection;